import { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Resolve upload URLs coming back from /api/upload (which return "/api/uploads/xxx")
// against the backend origin. Full http(s) URLs are passed through untouched.
// Kept here so this component is drop-in usable without pulling helpers from
// the admin editor module.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const resolveImg = (u) => {
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  if (u.startsWith('/api/uploads/')) return `${BACKEND_URL}${u}`;
  return u;
};

/**
 * CompetitionGallery
 *
 * Swipeable gallery for a competition's `gallery` array (list of image URLs).
 * Renders nothing if the list is empty — safe to drop into any competition
 * page without cluttering rackets that don't have secondary shots.
 *
 * Behaviour:
 *   - Snap-scroll swipe on touch (mobile), left/right arrow buttons on desktop.
 *   - Thumbnail row below the main image, click a thumb to jump to it.
 *   - Keyboard: ← / → cycle through slides when the region is focused.
 */
export const CompetitionGallery = ({ images = [] }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'center' });
  const [selected, setSelected] = useState(0);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i) => emblaApi?.scrollTo(i), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return undefined;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    onSelect();
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi]);

  if (!images || images.length === 0) return null;

  return (
    <div className="relative" data-testid="competition-gallery" aria-label="Competition gallery">
      <div className="relative rounded-2xl overflow-hidden border border-pd bg-[color:var(--pd-bg)]">
        <div ref={emblaRef} className="overflow-hidden">
          <div className="flex">
            {images.map((url, i) => (
              <div key={`${url}-${i}`} className="relative flex-[0_0_100%] aspect-[16/10]">
                <img
                  src={resolveImg(url)}
                  alt={`Gallery image ${i + 1} of ${images.length}`}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading={i === 0 ? 'eager' : 'lazy'}
                  data-testid={`competition-gallery-slide-${i}`}
                />
              </div>
            ))}
          </div>
        </div>
        {images.length > 1 ? (
          <>
            <button
              onClick={scrollPrev}
              className="absolute top-1/2 left-3 -translate-y-1/2 bg-[color:var(--pd-bg)]/85 hover:bg-[color:var(--pd-bg)] border border-pd rounded-full w-10 h-10 flex items-center justify-center text-[color:var(--pd-text)]"
              aria-label="Previous image"
              data-testid="competition-gallery-prev"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={scrollNext}
              className="absolute top-1/2 right-3 -translate-y-1/2 bg-[color:var(--pd-bg)]/85 hover:bg-[color:var(--pd-bg)] border border-pd rounded-full w-10 h-10 flex items-center justify-center text-[color:var(--pd-text)]"
              aria-label="Next image"
              data-testid="competition-gallery-next"
            >
              <ChevronRight size={18} />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-[color:var(--pd-bg)]/80 border border-pd rounded-full px-3 py-1 text-[11px] font-mono text-muted-sage">
              {selected + 1} / {images.length}
            </div>
          </>
        ) : null}
      </div>
      {images.length > 1 ? (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="competition-gallery-thumbs">
          {images.map((url, i) => (
            <button
              key={`thumb-${url}-${i}`}
              onClick={() => scrollTo(i)}
              aria-label={`Show image ${i + 1}`}
              className={`relative shrink-0 rounded-lg overflow-hidden border ${selected === i ? 'border-[color:var(--pd-accent-yellow)]' : 'border-pd hover:border-[color:var(--pd-gold)]/50'} w-20 h-14`}
            >
              <img src={resolveImg(url)} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default CompetitionGallery;
